import { DestroyRef, Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Router, NavigationEnd } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  Observable,
  combineLatest,
  defer,
  EMPTY,
  filter,
  distinctUntilChanged,
  switchMap,
  map,
  of,
  shareReplay,
  startWith,
  tap,
  catchError,
  retry,
  take,
} from "rxjs";
import {
  AbstractionLayerConfig,
  environment,
} from "../../environments/environment";
import { AuthService } from "./auth.service";
import { LaunchdarklyService } from "./api/launchdarkly.service";
import { ApolloClientService } from "./api/apollo-client.service";

@Injectable({ providedIn: "root" })
export class BrowserAnalyticsService {
  private sdkLoad$: Observable<void> | null = null;
  /** sessionUID + graphqlUrl — re-init when LaunchDarkly flips staging after first init. */
  private lastInitKey: string | null = null;

  constructor(
    private authService: AuthService,
    private launchdarklyService: LaunchdarklyService,
    private apolloClientService: ApolloClientService,
    private route: ActivatedRoute,
  ) {}

  start(router: Router, destroyRef: DestroyRef): void {
    this.loadSdkOnce()
      .pipe(
        retry({ count: 2, delay: 1500 }),
        catchError(() => of(undefined)),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe();

    combineLatest({
      userData: this.authService.userData$,
      launchDarklyFlags: this.launchdarklyService.launchDarklyFlagsWithFallback$,
      /** No one-time token left — login/token exchange has finished. */
      authSettled: combineLatest([
        this.route.queryParamMap,
        router.events.pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          map(() => null),
          startWith(null),
        ),
      ]).pipe(
        map(() => !this.authService.getQueryParam(environment.oneTimeTokenName)),
        distinctUntilChanged(),
      ),
    })
      .pipe(
        filter(
          ({ userData, authSettled }) =>
            authSettled && !!userData.sessionUID && !!userData.isLoggedIn,
        ),
        distinctUntilChanged(
          (a, b) =>
            a.userData.sessionUID === b.userData.sessionUID &&
            a.launchDarklyFlags["staging"] === b.launchDarklyFlags["staging"],
        ),
        // Same gate as a successful refresh: Apollo client exists (LD + session + login done).
        switchMap((payload) =>
          this.apolloClientService.apolloClient$.pipe(
            take(1),
            switchMap(() =>
              this.loadSdkOnce().pipe(
                filter(() => !!window.AnalyticsSDK),
                map(() => payload),
              ),
            ),
          ),
        ),
        tap(({ userData, launchDarklyFlags }) => {
          const sid = userData.sessionUID as string;
          const useStaging = launchDarklyFlags["staging"] !== false;
          const graphqlUrl = useStaging
            ? AbstractionLayerConfig.stagingUri
            : AbstractionLayerConfig.releaseUri;
          const initKey = `${sid}|${graphqlUrl}`;

          if (this.lastInitKey === initKey) {
            return;
          }
          this.lastInitKey = initKey;

          window.AnalyticsSDK!.init({
            appId: environment.analyticsAppId,
            graphqlUrl,
            sessionId: sid,
            getUsername: () => localStorage.getItem("userName") ?? "",
            autoTrackApiRequests: true,
          });

          window.AnalyticsSDK!.track("page_view", {
            title: document.title,
          });
        }),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe();

    router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe(() => {
        window.AnalyticsSDK?.track("page_view", {
          title: document.title,
        });
      });
  }

  track(eventName: string, extraData?: Record<string, unknown>): void {
    window.AnalyticsSDK?.track(eventName, extraData);
  }

  private loadSdkOnce(): Observable<void> {
    if (!this.sdkLoad$) {
      this.sdkLoad$ = defer(() => this.ensureSdkScript()).pipe(shareReplay(1));
    }
    return this.sdkLoad$;
  }

  private ensureSdkScript(): Observable<void> {
    if (typeof window === "undefined") {
      return of(undefined);
    }
    if (window.AnalyticsSDK) {
      return of(undefined);
    }
    const src = environment.analyticsSdkScriptUrl?.trim();
    if (!src) {
      return of(undefined);
    }
    return new Observable<void>((subscriber) => {
      const existing = document.querySelector(
        "script[data-fenix-analytics-sdk]",
      ) as HTMLScriptElement | null;
      const done = () => {
        subscriber.next();
        subscriber.complete();
      };
      const fail = () => {
        subscriber.error(new Error("analytics.sdk.js failed to load"));
      };
      if (existing) {
        if (window.AnalyticsSDK) {
          done();
          return;
        }
        existing.addEventListener("load", done, { once: true });
        existing.addEventListener("error", fail, { once: true });
        return;
      }
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.dataset["fenixAnalyticsSdk"] = "true";
      script.onload = () => done();
      script.onerror = () => fail();
      document.body.appendChild(script);
    });
  }
}
